import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { ProductOrderDto } from '@services/dtos'
import { findNewProductsReceived } from '@state/reducers/productOrderReducer'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  Card,
  CardContent,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Box,
  Alert,
} from '@mui/material'
import moment from 'moment'

interface Props {
  order: ProductOrderDto
}

export const ProductOrderDiscrepanciesInfo = ({ order }: Props) => {
  const { newProductsReceived, loadingNewProductsReceived } = useSelector(({ productOrder }) => ({
    newProductsReceived: productOrder.newProductsReceived.data,
    loadingNewProductsReceived: productOrder.newProductsReceived.loading,
  }))
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(findNewProductsReceived(order.id))
  }, [])

  if (loadingNewProductsReceived) return null

  const replacedQuantityByProductId = newProductsReceived.reduce((acc, { newProduct, quantity }) => {
    const baseProductId = newProduct.baseProduct.id
    acc[baseProductId] = (acc[baseProductId] ?? 0) + quantity
    return acc
  }, {} as Record<string, number>)

  const quantityDiscrepancies = order.lineItems.filter(item => item.remainingQuantity && item.remainingQuantity > 0)
  const missingCreditLines = quantityDiscrepancies
    .map(item => {
      const replaced = replacedQuantityByProductId[item.product.id] ?? 0
      const missing = Math.max((item.remainingQuantity ?? 0) - replaced, 0)
      const creditExclVat = missing * item.unitPriceExclVat
      const creditInclVat = creditExclVat * (1 + item.vatRate)
      return { ...item, missing, creditExclVat, creditInclVat }
    })
    .filter(item => item.missing > 0)

  const replacementCreditLines = newProductsReceived.map(({ newProduct, quantity }) => {
    const baseOrderProduct = order.lineItems.find(item => item.product.id === newProduct.baseProduct.id)
    const basePriceExclVat = baseOrderProduct?.unitPriceExclVat ?? 0
    const baseVatRate = baseOrderProduct?.vatRate ?? 0
    const replacementPriceExclVat = newProduct.product.priceExclVat
    const priceDifference = basePriceExclVat - replacementPriceExclVat
    const creditExclVat = priceDifference * quantity
    const creditInclVat = creditExclVat * (1 + baseVatRate)
    return {
      newProduct,
      quantity,
      basePriceExclVat,
      replacementPriceExclVat,
      priceDifference,
      creditExclVat,
      creditInclVat,
    }
  })

  const totalCreditExclVat =
    missingCreditLines.reduce((sum, l) => sum + l.creditExclVat, 0) +
    replacementCreditLines.reduce((sum, l) => sum + l.creditExclVat, 0)
  const totalCreditInclVat =
    missingCreditLines.reduce((sum, l) => sum + l.creditInclVat, 0) +
    replacementCreditLines.reduce((sum, l) => sum + l.creditInclVat, 0)

  return (
    <>
      {order.creditNoteSentAt && (
        <Alert className="mb-2">
          <strong>{'Demande d\'avoir envoyée'}</strong> le{' '}
          {moment(order.creditNoteSentAt).format('DD/MM/YYYY HH:mm')} à{' '}
          {order.creditNoteEmails?.join(', ')}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            {missingCreditLines.length > 0 && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" fontWeight={600}>
                    Produits partiellement reçus
                  </Typography>
                  <Chip size="small" label={missingCreditLines.length} />
                </Stack>

                <List dense disablePadding>
                  {missingCreditLines.map(({ product, quantity, missing, unitPriceExclVat, creditExclVat }) => (
                    <ListItem key={product.id} disableGutters>
                      <ListItemText
                        primary={product.name}
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" label={`Quantité initiale ${quantity}`} />
                            <Chip size="small" color="warning" label={`Manquant ${missing}`} />
                            <Chip size="small" variant="outlined" label={`PU HT ${unitPriceExclVat.toFixed(2)} €`} />
                            <Chip size="small" color="error" variant="outlined" label={`Avoir ${creditExclVat.toFixed(2)} € HT`} />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}

            {(missingCreditLines.length > 0 && replacementCreditLines.length > 0) && (
              <Divider />
            )}

            {replacementCreditLines.length > 0 && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" fontWeight={600}>
                    Produits de remplacement
                  </Typography>
                  <Chip size="small" label={replacementCreditLines.length} />
                </Stack>

                <List dense disablePadding>
                  {replacementCreditLines.map(({ newProduct, quantity, basePriceExclVat, replacementPriceExclVat, creditExclVat }) => (
                    <ListItem key={uuidv4()} disableGutters>
                      <ListItemText
                        primary={`${newProduct.baseProduct.name} → ${newProduct.product.name}`}
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" label={`Quantité ${quantity}`} />
                            <Chip size="small" variant="outlined" label={`PU commandé ${basePriceExclVat.toFixed(2)} €`} />
                            <Chip size="small" variant="outlined" label={`PU reçu ${replacementPriceExclVat.toFixed(2)} €`} />
                            <Chip size="small" color="error" variant="outlined" label={`Avoir ${creditExclVat.toFixed(2)} € HT`} />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}

            {(missingCreditLines.length > 0 || replacementCreditLines.length > 0) && (
              <>
                <Divider />
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontWeight={600}>
                      Total avoir
                    </Typography>
                    <Stack alignItems="flex-end">
                      <Typography variant="body1" fontWeight={600}>
                        {totalCreditExclVat.toFixed(2)} € HT
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {totalCreditInclVat.toFixed(2)} € TTC
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </>
  )
}

export default ProductOrderDiscrepanciesInfo
